import React, { useCallback, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AmenitiesData,
  Amenity,
  FormAmenityOutput,
  CATEGORY_NAMES,
} from "./amenities-form.types";

// Utility functions for input masks and validation
const formatCurrency = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, "");

  if (!digits) return "";

  // Convert to number and format as currency
  const number = parseFloat(digits) / 100;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(number);
};

const formatNumber = (value: string): string => {
  // Remove all non-digits
  return value.replace(/\D/g, "");
};

const validateCurrency = (value: string): boolean => {
  if (!value) return false;
  const digits = value.replace(/\D/g, "");
  const number = parseFloat(digits) / 100;
  return number > 0;
};

const validateNumber = (value: string): boolean => {
  if (!value) return false;
  const number = parseInt(value, 10);
  return !isNaN(number) && number > 0;
};

const validateText = (value: string): boolean => {
  return value.trim().length > 0;
};

interface AmenitiesFormProps {
  data: AmenitiesData;
  selectedAmenities: FormAmenityOutput[];
  onAmenitiesChange: (amenities: FormAmenityOutput[]) => void;
  className?: string;
}

interface StatusButtonProps {
  status: "not-included" | "included" | "charged";
  currentStatus: "not-included" | "included" | "charged";
  onClick: () => void;
  children: React.ReactNode;
}

const StatusButton: React.FC<StatusButtonProps> = ({
  status,
  currentStatus,
  onClick,
  children,
}) => {
  const isSelected = currentStatus === status;

  const baseClasses =
    "h-8 px-3 text-xs font-medium rounded-md transition-colors border";

  let buttonStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    color: "#374151",
    borderColor: "#d1d5db",
  };

  let hoverClasses = "hover:bg-gray-50";

  if (isSelected) {
    hoverClasses = "hover:opacity-90";
    if (status === "included") {
      buttonStyle = {
        backgroundColor: "#DCFCE7",
        color: "#016630",
        borderColor: "#B9F8CF",
      };
    } else if (status === "charged") {
      buttonStyle = {
        backgroundColor: "#FEF9C2",
        color: "#894B00",
        borderColor: "#FFF085",
      };
    } else {
      buttonStyle = {
        backgroundColor: "#FFE2E2",
        color: "#9F0712",
        borderColor: "#FFC9C9",
      };
    }
  }

  return (
    <button
      type="button"
      className={`${baseClasses} ${hoverClasses}`}
      style={buttonStyle}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const AmenitiesForm: React.FC<AmenitiesFormProps> = ({
  data,
  selectedAmenities,
  onAmenitiesChange,
  className,
}) => {
  // State for alert modals
  const [wifiAlert, setWifiAlert] = useState({
    isOpen: false,
    pendingAction: null as (() => void) | null,
  });

  const [airCondAlert, setAirCondAlert] = useState({
    isOpen: false,
    pendingAction: null as (() => void) | null,
  });

  // Create a status map for better performance
  const statusMap = useMemo(() => {
    const map = new Map<string, "not-included" | "included" | "charged">();
    selectedAmenities.forEach((amenity) => {
      map.set(amenity.amenityId, amenity.status);
    });
    return map;
  }, [selectedAmenities]);

  const getAmenityAttributes = useCallback(
    (amenityId: string) => {
      const amenity = selectedAmenities.find((a) => a.amenityId === amenityId);
      return amenity?.attributes || {};
    },
    [selectedAmenities],
  );

  const performStatusUpdate = useCallback(
    (amenityId: string, status: "not-included" | "included" | "charged") => {
      const existingIndex = selectedAmenities.findIndex(
        (a) => a.amenityId === amenityId,
      );
      const updated = [...selectedAmenities];

      if (existingIndex >= 0) {
        updated.splice(existingIndex, 1);
      }

      if (status !== "not-included") {
        const currentAttributes =
          selectedAmenities.find((a) => a.amenityId === amenityId)
            ?.attributes || {};
        updated.push({
          amenityId,
          status,
          attributes: currentAttributes,
        });
      }

      onAmenitiesChange(updated);
    },
    [selectedAmenities, onAmenitiesChange],
  );

  const updateAmenityStatus = useCallback(
    (amenityId: string, status: "not-included" | "included" | "charged") => {
      // Find amenity by ID to get its slug
      const amenity = data.amenities
        .flatMap((group) => group.amenities)
        .find((a) => a.id === amenityId);

      // Check if marking WiFi or air conditioning as unavailable
      if (status === "not-included" && amenity) {
        if (amenity.slug === "wi-fi") {
          // WiFi - show alert but don't allow to proceed
          setWifiAlert({
            isOpen: true,
            pendingAction: null, // No action since WiFi is mandatory
          });
          return; // Block the action completely
        }

        if (amenity.slug === "ar-condicionado") {
          // Air conditioning
          setAirCondAlert({
            isOpen: true,
            pendingAction: () => performStatusUpdate(amenityId, status),
          });
          return;
        }
      }

      // For other amenities or other status changes, proceed normally
      performStatusUpdate(amenityId, status);
    },
    [performStatusUpdate, data],
  );

  const updateAmenityAttribute = useCallback(
    (
      amenityId: string,
      attributeName: string,
      value: string,
      contentType: string,
    ) => {
      // Apply formatting based on content type
      let formattedValue = value;
      if (contentType === "price") {
        formattedValue = formatCurrency(value);
      } else if (contentType === "number") {
        formattedValue = formatNumber(value);
      }

      const amenityIndex = selectedAmenities.findIndex(
        (a) => a.amenityId === amenityId,
      );
      const updated = [...selectedAmenities];

      if (amenityIndex >= 0) {
        updated[amenityIndex] = {
          ...updated[amenityIndex],
          attributes: {
            ...updated[amenityIndex].attributes,
            [attributeName]: formattedValue,
          },
        };
      } else {
        updated.push({
          amenityId,
          status: "charged",
          attributes: { [attributeName]: formattedValue },
        });
      }

      onAmenitiesChange(updated);
    },
    [selectedAmenities, onAmenitiesChange],
  );

  const renderAttributes = (amenity: Amenity, currentStatus: string) => {
    if (currentStatus !== "charged" || amenity.attributes.length === 0) {
      return null;
    }

    const attributes = getAmenityAttributes(amenity.id);

    return (
      <div className="mt-2">
        {amenity.attributes.map((attr, index) => {
          const value = attributes[attr.name] || "";

          // Validate the current value
          let isValid = true;
          let errorMessage = "";

          if (value) {
            if (attr.contentType === "price") {
              isValid = validateCurrency(value);
              errorMessage = !isValid ? "Valor deve ser maior que R$ 0,00" : "";
            } else if (attr.contentType === "number") {
              isValid = validateNumber(value);
              errorMessage = !isValid ? "Número deve ser maior que 0" : "";
            } else {
              isValid = validateText(value);
              errorMessage = !isValid ? "Campo obrigatório" : "";
            }
          }

          return (
            <div key={index} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-gray-600 mr-2">
                  {attr.contentType === "price"
                    ? "Valor"
                    : attr.contentType === "number"
                      ? "Quantidade"
                      : "Descrição"}
                  :
                </Label>
                <div className="flex-1 w-full">
                  <Input
                    type="text" // Always text to handle formatting
                    placeholder={
                      attr.contentType === "price"
                        ? "R$ 0,00"
                        : attr.contentType === "number"
                          ? "1, 2, 3..."
                          : "Digite aqui"
                    }
                    value={value}
                    onChange={(e) =>
                      updateAmenityAttribute(
                        amenity.id,
                        attr.name,
                        e.target.value,
                        attr.contentType,
                      )
                    }
                    className={cn(
                      "h-8 text-xs",
                      !isValid && value
                        ? "border-red-500 focus:border-red-500"
                        : "",
                    )}
                  />
                  {!isValid && value && (
                    <p className="text-xs text-red-500 mt-1">{errorMessage}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Return early if data is not available
  if (!data || !data.amenities) {
    return (
      <div className={cn("space-y-6", className)}>
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Carregando amenidades...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      {data.amenities.map((group) => {
        const sectionName = group.name;
        const sectionTitle = CATEGORY_NAMES[sectionName] || sectionName;

        return (
          <div key={sectionName} className="bg-white rounded-lg border">
            <div className="px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {sectionTitle}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Controle de acesso e medidas de segurança disponíveis.
                </p>
              </div>
            </div>

            <div className="px-6 pb-4 border-t border-gray-100">
              <div className="space-y-4 pt-4">
                {group.amenities.map((amenity) => {
                  const currentStatus =
                    statusMap.get(amenity.id) || "not-included";

                  return (
                    <div
                      key={amenity.id}
                      data-amenity-id={amenity.id}
                      className="space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <Label className="text-sm font-medium text-gray-900">
                            {amenity.name}
                          </Label>
                          {amenity.description && (
                            <p className="text-xs text-gray-500 mt-1">
                              {amenity.description}
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2 ml-4">
                          <StatusButton
                            status="included"
                            currentStatus={currentStatus}
                            onClick={() =>
                              updateAmenityStatus(amenity.id, "included")
                            }
                          >
                            ✓ Incluso
                          </StatusButton>

                          <StatusButton
                            status="charged"
                            currentStatus={currentStatus}
                            onClick={() =>
                              updateAmenityStatus(amenity.id, "charged")
                            }
                          >
                            $ Cobrado à parte
                          </StatusButton>

                          <StatusButton
                            status="not-included"
                            currentStatus={currentStatus}
                            onClick={() =>
                              updateAmenityStatus(amenity.id, "not-included")
                            }
                          >
                            ✕ Indisponível
                          </StatusButton>
                        </div>
                      </div>

                      {renderAttributes(amenity, currentStatus)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}

      {/* WiFi Alert Modal */}
      <Dialog
        open={wifiAlert.isOpen}
        onOpenChange={(open) =>
          !open && setWifiAlert({ isOpen: false, pendingAction: null })
        }
      >
        <DialogContent className="max-w-md p-0 overflow-hidden border-0">
          <div className="bg-red-500 text-white p-4 rounded-lg">
            <div className="flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-medium text-sm mb-1">
                    Wi-Fi é obrigatório!
                  </h3>
                  <p className="text-sm opacity-90">
                    Sem internet não é possível
                    <br />
                    cadastrar sua sala.
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="bg-red-600 text-white hover:bg-red-700 text-xs px-3 py-1 h-auto"
                  onClick={() => {
                    // Find WiFi amenity and scroll to it
                    const wifiAmenity = data.amenities
                      .flatMap((group) => group.amenities)
                      .find((a) => a.slug === "wi-fi");

                    if (wifiAmenity) {
                      // Close modal first
                      setWifiAlert({ isOpen: false, pendingAction: null });

                      // Small delay to ensure modal is closed, then scroll to WiFi amenity
                      setTimeout(() => {
                        const wifiElement = document.querySelector(
                          `[data-amenity-id="${wifiAmenity.id}"]`,
                        );
                        if (wifiElement) {
                          wifiElement.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });

                          // Highlight the element briefly
                          wifiElement.classList.add(
                            "ring-2",
                            "ring-blue-500",
                            "ring-opacity-75",
                          );
                          setTimeout(() => {
                            wifiElement.classList.remove(
                              "ring-2",
                              "ring-blue-500",
                              "ring-opacity-75",
                            );
                          }, 2000);
                        }
                      }, 300);
                    }
                  }}
                >
                  Adicionar Wi-Fi
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Air Conditioning Alert Modal */}
      <Dialog
        open={airCondAlert.isOpen}
        onOpenChange={(open) =>
          !open && setAirCondAlert({ isOpen: false, pendingAction: null })
        }
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Alertas</DialogTitle>
            <DialogDescription>
              Usuário marcou ar-condicionado como indisponível
            </DialogDescription>
          </DialogHeader>
          <div className="bg-white border p-3 rounded-md text-sm text-gray-700">
            <p className="font-medium">Cadastrar sem ar-condicionado?</p>
            <p className="text-gray-600 mt-1">
              Salas sem ar-condicionado têm pode reduzir o<br />
              interesse dos clientes.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                // Find air conditioning amenity and scroll to it
                const airCondAmenity = data.amenities
                  .flatMap((group) => group.amenities)
                  .find((a) => a.slug === "ar-condicionado");

                if (airCondAmenity) {
                  // Close modal first
                  setAirCondAlert({ isOpen: false, pendingAction: null });

                  // Small delay to ensure modal is closed, then scroll to air conditioning amenity
                  setTimeout(() => {
                    const airCondElement = document.querySelector(
                      `[data-amenity-id="${airCondAmenity.id}"]`,
                    );
                    if (airCondElement) {
                      airCondElement.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });

                      // Highlight the element briefly
                      airCondElement.classList.add(
                        "ring-2",
                        "ring-blue-500",
                        "ring-opacity-75",
                      );
                      setTimeout(() => {
                        airCondElement.classList.remove(
                          "ring-2",
                          "ring-blue-500",
                          "ring-opacity-75",
                        );
                      }, 2000);
                    }
                  }, 300);
                } else {
                  setAirCondAlert({ isOpen: false, pendingAction: null });
                }
              }}
            >
              Voltar e corrigir
            </Button>
            <Button
              variant="default"
              className="bg-black text-white hover:bg-gray-800"
              onClick={() => {
                if (airCondAlert.pendingAction) {
                  airCondAlert.pendingAction();
                }
                setAirCondAlert({ isOpen: false, pendingAction: null });
              }}
            >
              Continuar sem ar-condicionado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { AmenitiesForm };
